import React, { useState, useEffect } from 'react';
import { Button, Col, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import viewIcon from '../../assets/images/icons/viewModalIcon.svg';
import editIcon from '../../assets/images/icons/icon-edit.svg';
import delIcon from '../../assets/images/icons/icon-delete.svg';
import { WebApiCalls } from '../../webApiCalls/WebApiCalls';
import { useAuth } from '../../hooks/useAuth';
import CommonModal from '../Modals/CommonModal';
import { toast } from 'react-toastify';
import warningIcon from '../../assets/images/status-icon.svg';
import decryptData from '../../helpers/Decryption';

const MenuItems = () => {
    const navigate = useNavigate();
    const [menuItems, setMenuItems] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
    const [loading, setLoading] = useState(false);
    const [isFilterSelected, setIsFilterSelected] = useState(false);
    const [buttonDisable, setButtonDisable] = useState(true);
    const [showBulkDel, setShowBulkDel] = useState(false);
    const [changeStatusModal, setChangeStatusModal] = useState(false);
    const [showDelModal, setShowDelModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const { getToken } = useAuth();

    // Fetch menu items
    const fetchMenuItems = async (page = 1, pageSize = 10, search = '', status = 'active') => {
        setLoading(true);
        const token = getToken();
        if (token) {
            const res = await new WebApiCalls().getMenuItems(token, pageSize, page, search, status);
            if (res?.code === 200) {
                const decryptedData = await decryptData(res?.data);
                setMenuItems(decryptedData || []);
                setPagination({ page, pageSize, total: res?.totalRecord || 0 });
            }
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchMenuItems();
    }, []);

    const searchDebounce = (value) => {
        clearTimeout(window.searchTimeout);
        window.searchTimeout = setTimeout(() => {
            fetchMenuItems(pagination.page, pagination.pageSize, value);
        }, 500);
    };

    // Delete single menu item
    const handleDelete = async () => {
        const token = getToken();
        if (token && selectedItem?._id) {
            const res = await new WebApiCalls().deleteMenuItem(token, selectedItem._id);
            if (res?.code === 200) {
                toast.success('Menu item deleted successfully.');
                fetchMenuItems(pagination.page, pagination.pageSize);
            } else {
                toast.error('Failed to delete menu item.');
            }
        }
        setShowDelModal(false);
    };

    // Bulk delete (optional, if backend supports)
    const deleteBulk = async () => {
        // ...implement if backend supports bulk delete...
        setShowBulkDel(false);
    };

    const updateSingle = async (menuItemId, body) => {
        try {
            setLoading(true);
            const token = getToken();
            if (token) {
                const res = await new WebApiCalls().updateMenuItem(token, menuItemId, body);
                if (res?.code === 200) {
                    toast.success('Menu Item status updated successfully.');
                    fetchMenuItems();
                } else {
                    toast.error('Failed to update menu item status.');
                }
            }
        } catch (error) {
            console.error('Error updating menu item:', error);
        } finally {
            setLoading(false);
        }
    };

    // Enable/disable bulk actions
    useEffect(() => {
        setButtonDisable(!(selectedRows && selectedRows.length > 0));
    }, [selectedRows]);

    const columns = [
        {
            name: (
                <h6 className="text-capitalize mt-2">
                    <span className="fw-bold">Item Name</span>
                </h6>
            ),
            selector: (row) => row.itemName,
            sortable: true,
            center: true
        },
        {
            name: (
                <h6 className="text-capitalize mt-2 text-center">
                    <span className="fw-bold">Type</span>
                </h6>
            ),
            selector: (row) => (row.type === 'price' ? 'Price' : 'Discount'),
            sortable: true,
            center: true
        },
        {
            name: (
                <h6 className="text-capitalize mt-2 text-center">
                    <span className="fw-bold">Value</span>
                </h6>
            ),
            selector: (row) => (row.type === 'price' ? row.price : `${row.discountPercent}%`),
            sortable: true,
            center: true
        },
        {
            name: (
                <h6 className="text-capitalize mt-2 text-center">
                    <span className="fw-bold">Status</span>
                </h6>
            ),
            selector: (row) => (
                <select
                    value={row?.status}
                    onChange={(e) => updateSingle(row?._id, { status: e.target.value })}
                    className={row?.status === 'inactive' ? 'status-selector-inactive' : 'status-selector'}
                >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
            ),
            center: true
        },
        {
            name: (
                <h6 className="text-capitalize mt-2 text-center">
                    <span className="fw-bold">Action</span>
                </h6>
            ),
            selector: (row) => (
                <div className="d-flex">
                    <OverlayTrigger delay={{ show: 300, hide: 0 }} placement={'bottom'} overlay={<Tooltip id={`tooltip-bottom`}>View details</Tooltip>}>
                        <div className="view-detail">
                            <img
                                key={row?._id}
                                width={15}
                                className="me-3"
                                src={viewIcon}
                                alt=""
                                onClick={() => {
                                    setSelectedItem(row);
                                    navigate('/menu-items/menu-item-details', {
                                        state: {
                                            detail: row,
                                            id: row._id,
                                            redirect_url: '/menu-items'
                                        }
                                    });
                                }}
                            />
                        </div>
                    </OverlayTrigger>
                    <OverlayTrigger delay={{ show: 300, hide: 0 }} placement={'bottom'} overlay={<Tooltip id={`tooltip-bottom`}>Edit</Tooltip>}>
                        <div className="edit-detail">
                            <img
                                width={15}
                                className="me-3"
                                src={editIcon}
                                alt=""
                                onClick={() => {
                                    navigate('/menu-items/edit-menu-item', {
                                        state: {
                                            id: row._id,
                                            prefill: row,
                                            redirect_url: '/menu-items'
                                        }
                                    });
                                }}
                            />
                        </div>
                    </OverlayTrigger>
                    <OverlayTrigger delay={{ show: 300, hide: 0 }} placement={'bottom'} overlay={<Tooltip id={`tooltip-bottom`}>Delete</Tooltip>}>
                        <div className="del-detail">
                            <img
                                width={15}
                                src={delIcon}
                                alt=""
                                onClick={() => {
                                    setSelectedItem(row);
                                    setShowDelModal(true);
                                }}
                            />
                        </div>
                    </OverlayTrigger>
                </div>
            ),
            center: true
        }
    ];

    const updateBulk = async (status) => {
        try {
            setLoading(true);
            const token = getToken();
            let obj = {
                _ids: selectedRows,
                status: status
            };
            if (token)
                new WebApiCalls()
                    .updateBulkMenuItems(token, obj)
                    .then((res) => {
                        if (res?.code === 200) {
                            fetchMenuItems();
                            setLoading(false);
                            toast.success('Branches are Updated Successfully.');
                            setToggleCleared(true);
                            setSelectedRows([]);
                        }
                    })
                    .catch((err) => {
                        setLoading(false);
                    });
            setLoading(false);
        } catch (error) {
            console.log('ERROR while updating Bulk', error);
        }
    };

    const handleClose = () => {
        setShowDelModal(false);
        setShowBulkDel(false);
        setChangeStatusModal(false);
    };

    return (
        <div style={{ padding: '20px' }}>
            <CommonModal
                handleClose={() => setShowDelModal(false)}
                show={showDelModal}
                handleDelete={handleDelete}
                text={'Are you sure, you want to delete this Menu Item?'}
            />
            <CommonModal
                handleClose={handleClose}
                show={showBulkDel}
                handleDelete={deleteBulk}
                text={selectedRows?.length > 1 ? 'Are you sure, you want to delete selected Menu Items?' : 'Are you sure, you want to delete selected Munu Item?'}
            />
            <Row className="mb-4 mt-1">
                <Col sm={3} md={3}>
                    <input
                        type={'search'}
                        className="search-input-bar"
                        placeholder={'Search here...'}
                        onChange={(e) => {
                            searchDebounce(e.target.value);
                        }}
                    />
                </Col>
                <Col sm={4} md={4} style={{ paddingLeft: 0 }}>
                    <div className="d-flex gap-10 justify-content-start my-3 my-sm-0">
                        <select
                            onChange={(e) => {
                                fetchMenuItems(undefined, undefined, '', e.target.value);
                                setIsFilterSelected(e.target.value ? true : false);
                            }}
                            className="status-selector w-120"
                            style={{ color: !isFilterSelected ? '#9a9a9a' : '' }}
                        >
                            <option value={''} hidden disabled>
                                Status
                            </option>
                            <option value={'active'}>Active</option>
                            <option value={'inactive'}>Inactive</option>
                        </select>
                    </div>
                </Col>
                <Col sm={5} md={5}>
                    <div className="d-flex gap-10 justify-content-end h-100">
                        {!buttonDisable && (
                            <>
                                <Button
                                    className="add-record-btn justify-content-center w-140"
                                    style={{ background: '#F3D119', color: 'black' }}
                                    onClick={() => setChangeStatusModal(true)}
                                    disabled={buttonDisable}
                                >
                                    Change Status
                                </Button>
                                <Button
                                    className="add-record-btn justify-content-center w-140"
                                    style={{ background: 'red' }}
                                    onClick={() => setShowBulkDel(true)}
                                    disabled={buttonDisable}
                                >
                                    Delete
                                </Button>
                            </>
                        )}
                        <Button
                            className="add-record-btn justify-content-center w-140"
                            style={{ background: '#13aa61' }}
                            onClick={() =>
                                navigate('/menu-items/add-menu-item', {
                                    state: {
                                        redirect_url: '/menu-items'
                                    }
                                })
                            }
                        >
                            Add Menu Item
                        </Button>
                    </div>
                </Col>
            </Row>
            <div className="menu-items-table-wrapper" style={{ overflowX: 'auto' }}>
                <DataTable
                    data={menuItems}
                    columns={columns}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={pagination.total}
                    onChangeRowsPerPage={(pageSize) => setPagination((prev) => ({ ...prev, pageSize }))}
                    onChangePage={(page) => setPagination((prev) => ({ ...prev, page }))}
                    selectableRows
                    onSelectedRowsChange={(state) => setSelectedRows(state.selectedRows.map((item) => item._id))}
                    clearSelectedRows={toggleCleared}
                />
            </div>
            <Modal centered show={changeStatusModal} onHide={handleClose} size="md">
                <Modal.Header closeButton onClick={handleClose}>
                    <Modal.Title className="text-center">
                        <h6 className="mt-2 modal-detail">Change Status</h6>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <div className="icon-warning-wrapper my-1 mt-1">
                        <img src={warningIcon} alt="" />
                    </div>
                    <p className="mx-4 my-4 px-5">Are you sure you want to change status?</p>
                    <div className="text-content-wrapper mt-3">
                        <div>
                            <Button
                                className="add-record-btn  justify-content-center w-140"
                                style={{ background: '#198754' }}
                                onClick={() => {
                                    setChangeStatusModal(false);
                                    updateBulk('active');
                                }}
                            >
                                Active
                            </Button>
                        </div>
                        <div>
                            <Button
                                className="add-record-btn  justify-content-center w-140"
                                style={{ background: '#F32013' }}
                                onClick={() => {
                                    setChangeStatusModal(false);
                                    updateBulk('inactive');
                                }}
                            >
                                Inactive
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 mt-2"></Modal.Footer>
            </Modal>
        </div>
    );
};

export default MenuItems;
